import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { salvarFotoOffline, removerFotoOffline, isNetworkError } from "../lib/offlineFotos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const CATALOGO = ["Ethrel (estimulante)", "Pasta fungicida", "Protetor de chuva", "Faca de sangria", "Bica", "Caneca/Tigela", "Arame", "Amônia/Anticoagulante", "Adubo/Fertilizante", "Calcário", "Herbicida", "Combustível/Óleo", "EPI", "Ferramentas", "Outro"];

const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 1600;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

export default function NotaLeituraModal({ onClose, onDone, fotoInicial = null, offlineId = null, onFilaChange }) {
  const [foto, setFoto] = useState(null);
  const [lendo, setLendo] = useState(false);
  const [draft, setDraft] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const fileRef = useRef(null);
  const iniciou = useRef(false);

  const salvarOffline = async (dataUrl) => {
    try {
      await salvarFotoOffline("nota", { dataUrl });
      toast.info("📴 Sem conexão — foto da nota salva no aparelho para lançar depois");
      onFilaChange?.();
      onClose();
    } catch { toast.error("Não foi possível salvar a nota no aparelho"); }
  };

  const processar = async (dataUrl, daFila = false) => {
    if (!navigator.onLine) {
      if (daFila) { toast.error("Ainda sem conexão — tente quando o sinal voltar"); onClose(); }
      else await salvarOffline(dataUrl);
      return;
    }
    setFoto(dataUrl);
    setLendo(true);
    try {
      const r = await axios.post(`${API}/financeiro/ler-nota`, { photo_base64: dataUrl }, { timeout: 90000 });
      setDraft({
        tipo: r.data.tipo || "outro",
        data: r.data.data || "",
        contraparte: r.data.contraparte || "",
        itens: r.data.itens || [],
        peso_liquido_kg: r.data.venda?.peso_liquido_kg || 0,
        drc_pct: r.data.venda?.drc_pct || 0,
        preco_kg: r.data.venda?.preco_kg || 0,
        valor_total: r.data.valor_total || 0,
        forma_pagamento: r.data.forma_pagamento || "",
        status: r.data.a_prazo ? "a_receber" : "recebido",
        confianca: r.data.confianca || "",
        avisos: r.data.avisos || [],
      });
    } catch (err) {
      if (isNetworkError(err) && !daFila) {
        await salvarOffline(dataUrl);
      } else {
        toast.error(err.response?.data?.detail || "Não foi possível ler a nota. Tente outra foto.");
      }
      setFoto(null);
    } finally {
      setLendo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    if (fotoInicial && !iniciou.current) {
      iniciou.current = true;
      processar(fotoInicial, true);
    }
  }, [fotoInicial]); // eslint-disable-line

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await processar(await resizeImage(f));
    } catch { toast.error("Não foi possível ler a foto"); }
  };

  const setItem = (i, campo, valor) => {
    setDraft((d) => {
      const itens = [...d.itens];
      itens[i] = { ...itens[i], [campo]: valor };
      return { ...d, itens };
    });
  };

  const confirmar = async () => {
    if (!draft.valor_total || draft.valor_total <= 0) { toast.error("Confira o valor total"); return; }
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/financeiro/confirmar-nota`, {
        ...draft,
        valor_total: parseFloat(draft.valor_total) || 0,
        peso_liquido_kg: parseFloat(draft.peso_liquido_kg) || 0,
        drc_pct: parseFloat(draft.drc_pct) || 0,
        preco_kg: parseFloat(draft.preco_kg) || 0,
        itens: draft.itens.map((it) => ({
          ...it,
          quantidade: parseFloat(it.quantidade) || 0,
          preco_unitario: parseFloat(it.preco_unitario) || 0,
          total: parseFloat(it.total) || 0,
        })),
        photo_base64: foto,
      }, { timeout: 60000 });
      if (offlineId) {
        await removerFotoOffline(offlineId).catch(() => {});
        onFilaChange?.();
      }
      const est = r.data.estoque_atualizado || [];
      toast.success(est.length ? `Lançado no caixa + ${est.length} produto(s) no estoque 📦` : "Lançamento confirmado! ✅");
      onDone((draft.data || "").slice(0, 7));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao confirmar");
    } finally { setSalvando(false); }
  };

  const isVenda = draft?.tipo === "venda_borracha";
  const isCompra = draft?.tipo === "compra_insumos";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="nota-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-green-900">📸 Lançar por foto da nota</h3>
          <button onClick={onClose} className="text-gray-400 text-xl px-2" data-testid="nota-fechar">✕</button>
        </div>

        {!draft && (
          <>
            <p className="text-xs text-gray-600">Fotografe a nota, cupom ou romaneio. A IA lê, você confere e confirma — nada entra no caixa sem o seu OK.</p>
            {!navigator.onLine && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 text-[11px] text-amber-800" data-testid="nota-offline-aviso">
                📴 Sem conexão — a foto fica salva no aparelho e você lança quando o sinal voltar.
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="nota-foto-input" />
            {lendo ? (
              <div className="text-center py-6" data-testid="nota-lendo">
                <div className="animate-pulse text-sm font-semibold text-green-800">🔍 Lendo a nota... (~8 segundos)</div>
                {foto && <img src={foto} alt="Nota" className="mt-3 rounded-lg max-h-48 object-contain mx-auto" />}
              </div>
            ) : (
              <Button onClick={() => fileRef.current?.click()} className="w-full bg-green-700 hover:bg-green-800" data-testid="nota-escolher-btn">📷 Tirar foto / escolher da galeria</Button>
            )}
          </>
        )}

        {draft && (
          <div className="space-y-3" data-testid="nota-conferencia">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-blue-800 bg-blue-50 px-2 py-1 rounded">✏️ Confira e ajuste antes de confirmar</span>
              {draft.confianca && <span className={`text-[10px] px-2 py-0.5 rounded-full ${draft.confianca === "ALTA" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>Leitura: {draft.confianca}</span>}
            </div>

            {draft.avisos.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 text-[11px] text-amber-800" data-testid="nota-avisos">
                ⚠️ {draft.avisos.join(" · ")}
              </div>
            )}

            <div className="flex gap-1.5 flex-wrap">
              {[["venda_borracha", "💰 Venda de borracha"], ["compra_insumos", "📦 Compra de insumos"], ["outro", "📄 Outro"]].map(([v, l]) => (
                <button key={v} onClick={() => setDraft({ ...draft, tipo: v })}
                  className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${draft.tipo === v ? "bg-green-700 text-white border-green-700" : "bg-white text-gray-600"}`}
                  data-testid={`nota-tipo-${v}`}>{l}</button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={draft.data} onChange={(e) => setDraft({ ...draft, data: e.target.value })} data-testid="nota-data" />
              <Input placeholder={isVenda ? "Comprador/usina" : "Fornecedor"} value={draft.contraparte} onChange={(e) => setDraft({ ...draft, contraparte: e.target.value })} data-testid="nota-contraparte" />
            </div>

            {isVenda && (
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-[10px] text-gray-500">Peso líq. (kg)</label><Input type="number" step="any" value={draft.peso_liquido_kg} onChange={(e) => setDraft({ ...draft, peso_liquido_kg: e.target.value })} data-testid="nota-kg" /></div>
                <div><label className="text-[10px] text-gray-500">DRC %</label><Input type="number" step="any" value={draft.drc_pct} onChange={(e) => setDraft({ ...draft, drc_pct: e.target.value })} data-testid="nota-drc" /></div>
                <div><label className="text-[10px] text-gray-500">R$/kg</label><Input type="number" step="any" value={draft.preco_kg} onChange={(e) => setDraft({ ...draft, preco_kg: e.target.value })} data-testid="nota-preco-kg" /></div>
              </div>
            )}

            {isCompra && draft.itens.length > 0 && (
              <div className="space-y-2" data-testid="nota-itens">
                <div className="text-xs font-semibold text-gray-700">Itens (entram no estoque ao confirmar):</div>
                {draft.itens.map((it, i) => (
                  <div key={i} className="border rounded-lg p-2 space-y-1.5 bg-gray-50" data-testid={`nota-item-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-gray-500 truncate flex-1">{it.produto_original}</span>
                      <button onClick={() => setDraft((d) => ({ ...d, itens: d.itens.filter((_, j) => j !== i) }))} className="text-red-500 text-xs" data-testid={`nota-item-remove-${i}`}>✕</button>
                    </div>
                    <select value={it.produto_canonico} onChange={(e) => setItem(i, "produto_canonico", e.target.value)} className="w-full border rounded-lg px-2 py-1.5 text-xs bg-white" data-testid={`nota-item-produto-${i}`}>
                      {CATALOGO.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-1.5">
                      <Input type="number" step="any" className="text-xs h-8" placeholder="Qtd" value={it.quantidade} onChange={(e) => setItem(i, "quantidade", e.target.value)} data-testid={`nota-item-qtd-${i}`} />
                      <Input type="number" step="any" className="text-xs h-8" placeholder="R$ unit." value={it.preco_unitario} onChange={(e) => setItem(i, "preco_unitario", e.target.value)} data-testid={`nota-item-preco-${i}`} />
                      <Input type="number" step="any" className="text-xs h-8" placeholder="Total" value={it.total} onChange={(e) => setItem(i, "total", e.target.value)} data-testid={`nota-item-total-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 items-end">
              <div><label className="text-[10px] text-gray-500 font-semibold">VALOR TOTAL (R$)</label><Input type="number" step="any" value={draft.valor_total} onChange={(e) => setDraft({ ...draft, valor_total: e.target.value })} className="font-bold" data-testid="nota-valor-total" /></div>
              <Input placeholder="Forma de pagamento" value={draft.forma_pagamento} onChange={(e) => setDraft({ ...draft, forma_pagamento: e.target.value })} data-testid="nota-pagamento" />
            </div>

            {isVenda && (
              <div className="flex gap-2">
                {[["recebido", "💰 Já recebi"], ["a_receber", "⏳ A receber"]].map(([v, l]) => (
                  <button key={v} onClick={() => setDraft({ ...draft, status: v })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border ${draft.status === v ? "bg-blue-700 text-white border-blue-700" : "bg-white text-gray-600"}`}
                    data-testid={`nota-status-${v}`}>{l}</button>
                ))}
              </div>
            )}

            <div className="text-right text-sm font-bold text-green-800" data-testid="nota-resumo">{isVenda ? "+" : "-"}{BRL(parseFloat(draft.valor_total) || 0)}</div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setDraft(null); setFoto(null); }} className="flex-1" data-testid="nota-refazer">↩️ Outra foto</Button>
              <Button onClick={confirmar} disabled={salvando} className="flex-1 bg-green-700 hover:bg-green-800" data-testid="nota-confirmar">{salvando ? "Salvando..." : "✅ Confirmar lançamento"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
